import { Component, computed, effect, ElementRef, inject, OnDestroy, OnInit, Signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MAT_CHECKBOX_DEFAULT_OPTIONS, MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import {
    MatNestedTreeNode,
    MatTree,
    MatTreeNestedDataSource,
    MatTreeNodeDef,
    MatTreeNodeOutlet,
    MatTreeNodeToggle
} from '@angular/material/tree';
import { RedlineConfigService } from '@app/components/redline/services/redline-config.service';
import { RedlineVisibilityChanged } from '@app/components/redline/state/redline.actions';
import { RedlineLayerConfiguration, RedlineLegendNode } from '@app/components/redline/state/redline.model';
import { RedlineState } from '@app/components/redline/state/redline.state';
import { LegendIconDimensions } from '@core/state/appConfig/app-config.model';
import { AppConfigState } from '@core/state/appConfig/app-config.state';
import { Actions, ofActionSuccessful, Store } from '@ngxs/store';
import { LoggerService } from '@shared/services/logger.service';
import { RestService } from '@shared/services/rest.service';
import { isEqual } from 'lodash-es';
import { Observable, of, Subject } from 'rxjs';
import { concatMap, distinctUntilChanged, filter, map, mergeMap, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { MapConfigService } from '../services/map-config.service';
import { MapService } from '../services/map.service';
import { ActiveMapState } from '../state/active-map.state';
import { SetNodeExpandedState } from '../state/map-config.actions';
import { LegendNode, MsClassNode, MsGroupNode, MsLayerNode } from '../state/map-config.model';
import { MapConfigState } from '../state/map-config.state';
import { RedlineIconComponent } from './redline-icon.component';

@Component({
    selector: 'dash-gis-map-legend-container',
    templateUrl: './map-legend-container.component.html',
    styleUrls: [ './map-legend-container.component.scss' ],
    providers: [
        {
            provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
            useValue: { clickAction: 'check' }
        }
    ],
    imports: [
        MatTree,
        MatTreeNodeDef,
        MatNestedTreeNode,
        MatIconButton,
        MatTreeNodeToggle,
        MatIcon,
        MatCheckbox,
        FormsModule,
        MatTreeNodeOutlet,
        RedlineIconComponent
    ]
})
export class MapLegendContainerComponent implements OnInit, OnDestroy {
    mapChildAccessor = this.getChildren.bind(this);
    dataSource: MatTreeNestedDataSource<LegendNode>;
    redlineChildAccessor = this.getRedlineChildren.bind(this);
    redlineDataSource: MatTreeNestedDataSource<RedlineLegendNode>;
    mapTree = viewChild<MatTree<LegendNode>>('mapTree');

    private legendIcons$: Observable<LegendIconDimensions> = inject(Store).select(AppConfigState.legendIcons);
    private mapConfigLoaded$: Observable<boolean> = inject(Store).select(MapConfigState.loaded);
    private mapName$: Observable<string> = inject(Store).select(MapConfigState.mapName);
    private root: Signal<MsGroupNode> = inject(Store).selectSignal(MapConfigState.visibleRoot);
    private redlineLoaded$: Observable<boolean> = inject(Store).select(RedlineState.loaded);
    private currentScale$: Observable<number> = inject(Store).select(ActiveMapState.scale);

    private children: Signal<LegendNode[]>;
    private dispose$ = new Subject<void>();

    /**
     * @param elementRef {ElementRef}
     * @param logger {LoggerService}
     * @param restService {RestService}
     * @param mapConfig {MapConfigService}
     * @param redlineConfig {RedlineConfigService}
     * @param mapService {MapService}
     * @param actions$ {Actions}
     * @param store {Store}
     * @param cdr
     */
    constructor(
        elementRef: ElementRef,
        private logger: LoggerService,
        private restService: RestService,
        private mapConfig: MapConfigService,
        private redlineConfig: RedlineConfigService,
        private mapService: MapService,
        private actions$: Actions,
        private store: Store
    ) {
        this.dataSource = new MatTreeNestedDataSource<LegendNode>();
        this.redlineDataSource = new MatTreeNestedDataSource<RedlineLegendNode>();
        this.children = computed(() => this.getChildren(this.root()));
        effect(() => {
            this.dataSource.data = [ ...this.children() ];
        });
    }

    ngOnInit() {
        this.mapConfigLoaded$
            .pipe(
                distinctUntilChanged(),
                filter((b) => b),
                mergeMap(() => this.legendIcons$),
                withLatestFrom(this.mapName$),
                concatMap(([ legendIcons, mapName ]) => {
                    const width = legendIcons.width;
                    const height = legendIcons.height;
                    return this.restService.apiGet(`map/icons/${ mapName }/${ width }/${ height }`, null);
                }),
                tap((data: any) => {
                    if (data && data.css) {
                        const head = document.getElementsByTagName('head')[0];
                        const style = document.createElement('style');
                        style.appendChild(document.createTextNode(data.css));
                        head.appendChild(style);
                    }
                }),
                takeUntil(this.dispose$)
            )
            .subscribe();

        this.redlineLoaded$
            .pipe(
                distinctUntilChanged(isEqual),
                filter((b) => b),
                switchMap(() => this.currentScale$),
                switchMap((scale) => this.redlineConfig.getInRangeLayerList$(scale)),
                takeUntil(this.dispose$)
            )
            .subscribe((inRange) => {
                this.redlineDataSource.data = inRange;
            });

        this.actions$
            .pipe(
                ofActionSuccessful(RedlineVisibilityChanged),
                map(() => this.mapService.currentScale),
                switchMap((scale) => this.redlineConfig.getInRangeLayerList$(scale)),
                takeUntil(this.dispose$)
            )
            .subscribe((inRange) => {
                this.redlineDataSource.data = inRange;
            });
    }

    ngOnDestroy() {
        this.dispose$.next();
    }

    toggleNode(node) {
        const nodeType = MapLegendContainerComponent.getNodeType(node);
        this.store.dispatch(new SetNodeExpandedState(node.name, nodeType === 'group', !node.expand));
    }

    getChildren(node: LegendNode): LegendNode[] {
        switch (MapLegendContainerComponent.getNodeType(node)) {
            case 'group':
                const groupNode = node as MsGroupNode;
                if (groupNode.expand) {
                    this.mapTree()?.expand(groupNode);
                } else {
                    this.mapTree()?.collapse(groupNode);
                }
                const groupChildren: Array<MsGroupNode | MsLayerNode> = [];
                groupChildren.push(...groupNode.layers, ...groupNode.groups);
                return groupChildren.sort(
                    (a: MsLayerNode | MsGroupNode, b: MsLayerNode | MsGroupNode) => a.legendOrder - b.legendOrder
                );
            case 'layer':
                const layerNode = node as MsLayerNode;
                if (layerNode.expand) {
                    this.mapTree()?.expand(layerNode);
                } else {
                    this.mapTree()?.collapse(layerNode);
                }
                return layerNode.classes.map((klass: MsClassNode) => ({
                    ...klass,
                    layer: layerNode.name
                }));
            case 'class':
                return [];
        }
    }

    redlineHasMultipleClasses(index: number, node: RedlineLegendNode): boolean {
        switch (MapLegendContainerComponent.getRedlineNodeType(node)) {
            case 'layer':
                const layerNode = node as RedlineLayerConfiguration;
                return Object.getOwnPropertyNames(layerNode.classes).length > 1;
            case 'class':
                return false;
        }
    }

    redlineHasOneClass(index: number, node: RedlineLegendNode): boolean {
        switch (MapLegendContainerComponent.getRedlineNodeType(node)) {
            case 'layer':
                const layerNode = node as RedlineLayerConfiguration;
                return Object.getOwnPropertyNames(layerNode.classes).length === 1;
            case 'class':
                return false;
        }
    }

    getRedlineChildren(node: RedlineLegendNode): Observable<RedlineLegendNode[]> {
        switch (MapLegendContainerComponent.getRedlineNodeType(node)) {
            case 'layer':
                const layerNode = node as RedlineLayerConfiguration;
                const classes: RedlineLegendNode[] = [];
                Object.getOwnPropertyNames(layerNode.classes).forEach((className) => {
                    const redlineClass = layerNode.classes[className];
                    redlineClass.name = className;
                    redlineClass.parent = layerNode;
                    classes.push(redlineClass);
                });
                return of(classes);
            case 'class':
                return of([]);
        }
    }

    isGroup(index, node): boolean {
        return MapLegendContainerComponent.getNodeType(node) === 'group';
    }

    isExpandableGroup(index, node): boolean {
        return (
            MapLegendContainerComponent.getNodeType(node) === 'group' &&
            (node.groups.length > 0 || node.layers.length > 0) &&
            node.expandable
        );
    }

    isLayer(index, node): boolean {
        return MapLegendContainerComponent.getNodeType(node) === 'layer';
    }

    isExpandableLayer(index, node): boolean {
        return MapLegendContainerComponent.getNodeType(node) === 'layer' && node.classes.length > 1 && node.expandable;
    }

    isNonExpandableLayer(index, node): boolean {
        return MapLegendContainerComponent.getNodeType(node) === 'layer' && node.classes.length > 1 && !node.expandable;
    }

    toggleLayer(node: MsLayerNode) {
        if (node.status) {
            this.mapConfig.hideLayers(node.name);
        } else {
            this.mapConfig.showLayers(node.name);
        }
    }

    toggleGroup(node: MsGroupNode) {
        if (node.status) {
            this.mapConfig.hideGroup(node.name);
        } else {
            this.mapConfig.showGroup(node.name);
        }
    }

    toggleRedlineLayer(node: RedlineLayerConfiguration) {
        if (node.status) {
            this.redlineConfig.hideLayer(node.name);
        } else {
            this.redlineConfig.showLayer(node.name);
        }
    }

    trackByFn(index: number, node: LegendNode) {
        return node;
    }

    expansionKeyFn(node: MsLayerNode | MsGroupNode) {
        return node.name;
    }

    private static getNodeType(node: LegendNode) {
        if ((node as MsGroupNode).layers && (node as MsGroupNode).groups) {
            return 'group';
        } else if ((node as MsLayerNode).classes) {
            return 'layer';
        } else {
            return 'class';
        }
    }

    private static getRedlineNodeType(node: RedlineLegendNode) {
        if ((node as RedlineLayerConfiguration).classes) {
            return 'layer';
        } else {
            return 'class';
        }
    }

    private setExpansion(node: LegendNode) {
        if (node.hasOwnProperty('expand')) {
            if (node['expand']) {
                this.mapChildAccessor.expand(node);
            } else {
                this.mapChildAccessor.collapse(node);
            }

            const nodes = this.mapChildAccessor.getDescendants(node);
            for (let i = 0; i < nodes.length; i++) {
                this.setExpansion(nodes[i]);
            }
        }
    }
}
